import { useMemo } from 'react';
import { useGetLmsPreconditionsQuery, useGetLmsTrainingPreconditionsQuery, useUpdateLmsTrainingPreconditionsMutation } from '../../../../api';
import toastService from '../../../../utils/toastService';
import { formatErrorMessage } from '../../../../utils/errorUtils';
import { useLanguage } from '../../../../contexts/LanguageContext';

/**
 * Eğitim detayındaki "Ön Koşullar" sekmesi için veri hook'u.
 * Bkz. docs/LMS-Egitim-Katalogu-ve-Egitmenler-API 2.md §7.
 *
 * Seçim listesi `LmsPrecondition/GetList` ile doldurulur (tanımların `id`'si aktif dildeki
 * satırın id'sidir); mevcut seçim `LmsTrainingPrecondition/GetList` ile `trainingId` filtresiyle
 * okunur ve `preconditionCode` üzerinden eşleştirilir (satır id'leri farklı dil satırlarından
 * gelmiş olabileceği için eşleşme id değil kod bazında yapılır — doc §7.1). Kaydetme, seçili
 * tanım id'lerinin TAMAMINI `Update` ucuna gönderir; bu uç gönderilen listeyi son durum kabul eder.
 */
const useTrainingPreconditionsData = (trainingId) => {
    const { th } = useLanguage();

    const {
        data: allDefinitions,
        isLoading: isLoadingDefinitions,
        error: definitionsError
    } = useGetLmsPreconditionsQuery({ isActive: 1, pageNumber: 1, pageSize: 500 }, { refetchOnMountOrArgChange: true });

    const {
        data: trainingPreconditions,
        isLoading: isLoadingSelection,
        error: selectionError,
        refetch
    } = useGetLmsTrainingPreconditionsQuery({ trainingId, pageNumber: 1, pageSize: 500 }, { skip: !trainingId, refetchOnMountOrArgChange: true });

    const [updateMutation, { isLoading: isSaving }] = useUpdateLmsTrainingPreconditionsMutation();

    const options = useMemo(() => {
        const items = allDefinitions?.items || [];
        // `precondition`/`description` backend tarafından aktif dile göre döner (doc §6.1)
        return items.map((item) => ({
            id: item.id,
            code: item.code,
            label: item.precondition || item.code,
            description: item.description
        }));
    }, [allDefinitions]);

    const selectedIds = useMemo(() => {
        const selectedCodes = new Set((trainingPreconditions?.items || []).map((row) => row.preconditionCode));
        return options.filter((option) => selectedCodes.has(option.code)).map((option) => option.id);
    }, [trainingPreconditions, options]);

    const savePreconditions = async (nextSelectedIds) => {
        try {
            await updateMutation({ trainingId, preconditionIds: nextSelectedIds }).unwrap();
            toastService.success(th('trainingCatalog.preconditions.saveSuccess'), th('common.success'));
            refetch();
            return true;
        } catch (err) {
            toastService.error(formatErrorMessage(err) || th('trainingCatalog.preconditions.saveError'), th('common.error'));
            return false;
        }
    };

    return {
        options,
        selectedIds,
        isLoading: isLoadingDefinitions || isLoadingSelection,
        hasError: !!definitionsError || !!selectionError,
        isSaving,
        savePreconditions,
        refetch
    };
};

export default useTrainingPreconditionsData;
